import { ReactNode, useContext, useState } from 'react';
import { Avatar, Box, Dot, Drawer, Icon, Link, useResponsive } from '@bitrise/bitkit';

import { HEADER_HEIGHT } from '@/constants';
import useChangelog from '@/hooks/useChangelog';
import { segmentTrack } from '@/hooks/analytics/useTracking';
import BitbotLogoWithText from '../../PageHeader/PageHeaderContent/BitbotLogoWithText';
import { GlobalPropsContext } from '../../GlobalPropsContext';
import AccountDrawer from './Drawer/AccountDrawer';
import NotificationsDrawer from './Drawer/NotificationsDrawer';
import HelpDrawer from './Drawer/HelpDrawer';
import HeaderButton from './HeaderButton';
import SearchDrawer from './Drawer/SearchDrawer';
import SearchBar from './Search/SearchBar';
import ChangelogDrawer from './Drawer/ChangelogDrawer';

type PlatformHeaderProps = {
  onNavOpenClick: () => void;
  marginTop: string;
  children: ReactNode;
};

const PlatformHeader = ({ onNavOpenClick, marginTop, children }: PlatformHeaderProps): JSX.Element => {
  const { isMobile } = useResponsive();

  const { user, account } = useContext(GlobalPropsContext);

  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [changeLogRead, setChangeLogRead] = useState(false);
  const [drawerContent, setDrawerContent] = useState<keyof typeof drawerTitles>();

  const drawerTitles = {
    help: 'Help',
    notifications: 'Notifications & activity',
    account: 'Signed in as',
    search: 'Search in your workspaces',
    changelog: 'Changelog',
  };

  const toggleDrawer = (name: keyof typeof drawerTitles) => {
    if (drawerContent === name) {
      closeDrawers();
    } else {
      setDrawerContent(name);
    }
  };

  const closeDrawers = () => {
    setDrawerContent(undefined);
  };

  const { hasUnreadTopics: changelogHasUnreadTopics } = useChangelog();

  return (
    <Box
      as="header"
      className="platform-header"
      position="fixed"
      left={0}
      top={0}
      right={0}
      zIndex={300}
      marginTop={marginTop}
    >
      <Box
        display="flex"
        height={HEADER_HEIGHT}
        paddingX={[0, 20]}
        backgroundColor="sys/primary/emphasized"
        justifyContent="space-between"
        alignItems="center"
      >
        {isMobile && (
          <>
            {!account && (
              <HeaderButton title="Create Workspace">
                <Link href="/create-workspace">
                  <Icon name="PlusCircle" textColor="icon/on-color" />
                </Link>
              </HeaderButton>
            )}
            {account && (
              <HeaderButton
                title="Workspace"
                position="relative"
                display="flex"
                alignItems="center"
                paddingX="8"
                onClick={() => {
                  onNavOpenClick();
                  closeDrawers();
                }}
              >
                <Avatar
                  size="32"
                  borderWidth={1}
                  variant="workspace"
                  borderColor="border/minimal"
                  name={account.name}
                  src={account.avatarUrl}
                />
                <Box
                  backgroundColor="sys/primary/emphasized"
                  position="absolute"
                  right="0"
                  bottom="0"
                  borderRadius="3"
                  paddingX="2"
                >
                  <Icon display="block" size="16" name="MenuHamburger" textColor="icon/on-color" />
                </Box>
              </HeaderButton>
            )}
          </>
        )}
        <Box as="a" href="/">
          <BitbotLogoWithText />
        </Box>

        <Box display="flex">
          <Box position="relative">
            {!isSearchOpen && account && (
              <HeaderButton
                title="Search projects"
                className="platform-shell-search"
                isActive={isMobile && drawerContent === 'search'}
                onClick={() => {
                  if (!isMobile) {
                    setIsSearchOpen(true);
                    closeDrawers();
                  } else {
                    toggleDrawer('search');
                  }
                }}
              >
                <Icon name="Magnifier" textColor="icon/on-color" />
              </HeaderButton>
            )}
            {!isMobile && (
              <SearchBar
                position="absolute"
                top="3"
                right="0"
                transform={isSearchOpen ? undefined : 'translateX(5px)'}
                pointerEvents={isSearchOpen ? undefined : 'none'}
                opacity={isSearchOpen ? '1' : '0'}
                inputTabIndex={isSearchOpen ? 0 : -1}
                transition={
                  isSearchOpen ? 'all .1s ease-in, opacity .1s ease-in' : 'all .1s ease-in, opacity .05s ease-in'
                }
                onClose={() => setIsSearchOpen(false)}
              />
            )}
          </Box>
          {!isMobile && (
            <HeaderButton
              title="Changelog"
              isActive={drawerContent === 'changelog'}
              onClick={() => {
                if (drawerContent !== 'changelog') segmentTrack('Changelog Drawer Opened');
                toggleDrawer('changelog');
                setChangeLogRead(true);
              }}
            >
              <Icon name="News" textColor="icon/on-color" />
              {!changeLogRead && changelogHasUnreadTopics && (
                <Dot size={8} backgroundColor="icon/negative" position="absolute" top="12" right="12" />
              )}
            </HeaderButton>
          )}
          {!isMobile && (
            <HeaderButton title="Help" isActive={drawerContent === 'help'} onClick={() => toggleDrawer('help')}>
              <Icon name="Support" textColor="icon/on-color" />
            </HeaderButton>
          )}
          {!isMobile && account && (
            <HeaderButton
              title="Notifications"
              isActive={drawerContent === 'notifications'}
              onClick={() => toggleDrawer('notifications')}
            >
              <Icon name="Bell" textColor="icon/on-color" />
            </HeaderButton>
          )}
          <HeaderButton title="Account" isActive={drawerContent === 'account'} onClick={() => toggleDrawer('account')}>
            {!isMobile && (
              <Avatar
                className="platform-shell-account"
                borderRadius="24"
                size="32"
                borderWidth="1px"
                borderColor="border/minimal"
                name={user?.username || ''}
                src={user?.avatarUrl}
              />
            )}
            {isMobile && <Icon name="Person" textColor="icon/on-color" />}
          </HeaderButton>
        </Box>
      </Box>
      {children}
      <Drawer
        padding="24px 0"
        headerPadding="4px 24px 0px 24px"
        bodyPadding="8px 0 0 0"
        bodyProps={{
          sx: {
            height: '100%',
            overflow: 'auto',
          },
        }}
        isOpen={!!drawerContent}
        title={drawerContent ? drawerTitles[drawerContent] : ''}
        onClose={() => {
          setDrawerContent(undefined);
        }}
      >
        {drawerContent === 'search' && <SearchDrawer />}
        {drawerContent === 'account' && user && <AccountDrawer user={user} />}
        {drawerContent === 'notifications' && <NotificationsDrawer />}
        {drawerContent === 'help' && <HelpDrawer />}
        {drawerContent === 'changelog' && <ChangelogDrawer />}
      </Drawer>
    </Box>
  );
};

export default PlatformHeader;
